
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { PlusCircle } from 'lucide-react';
import { useGasDashboard } from '@/contexts/gas';

// Template options for new KPI cards
const kpiTemplates = [
  { id: 'efficiency', title: 'Gas Efficiency', description: 'Track gas efficiency metrics' },
  { id: 'costs', title: 'Gas Costs', description: 'Monitor gas cost trends' },
  { id: 'savings', title: 'Gas Savings', description: 'Track cost savings from efficiency measures' },
  { id: 'outages', title: 'Outages', description: 'Monitor gas outages and downtimes' },
  { id: 'gridLoad', title: 'Grid Load', description: 'Track load on the gas grid' },
  { id: 'greenScore', title: 'Green Score', description: 'Environmental impact score' },
];

const AddKpiCard: React.FC = () => {
  const { addKpiCard } = useGasDashboard();
  const [customTitle, setCustomTitle] = useState('');
  
  const handleAddTemplate = (template: { id: string; title: string }) => {
    addKpiCard({
      title: template.title,
      component: null,
      visible: true,
    });
  };
  
  const handleAddCustom = () => {
    if (customTitle.trim()) {
      addKpiCard({
        title: customTitle,
        component: null,
        visible: true,
      });
      setCustomTitle('');
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button className="w-full mb-4">
          <PlusCircle className="mr-2 h-4 w-4" />
          Add New KPI
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add New KPI Card</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Choose a template</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {kpiTemplates.map((template) => (
                <DialogClose key={template.id} asChild>
                  <Button 
                    variant="outline" 
                    className="justify-start h-auto py-3 text-left"
                    onClick={() => handleAddTemplate(template)}
                  >
                    <div>
                      <div className="font-medium">{template.title}</div>
                      <div className="text-xs text-muted-foreground">{template.description}</div>
                    </div>
                  </Button>
                </DialogClose>
              ))}
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="customTitle">Or create a custom KPI</Label>
            <div className="flex gap-2">
              <Input 
                id="customTitle" 
                placeholder="Enter KPI title" 
                value={customTitle} 
                onChange={(e) => setCustomTitle(e.target.value)}
              />
              <DialogClose asChild>
                <Button onClick={handleAddCustom} disabled={!customTitle.trim()}>Add</Button>
              </DialogClose>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddKpiCard;
